import { createTransformedImports, isBabelApi } from './utils.js';
function createSignaliumCallbackTransform(api, opts) {
    const transformedImports = createTransformedImports([
        ['component', ['signalium/react']],
        ['reactive', ['signalium']],
        ['reactiveMethod', ['signalium']],
        ['relay', ['signalium']],
        ['task', ['signalium']],
        ['watcher', ['signalium']],
        ['useReactive', ['signalium/react']],
        ['useReactiveShallow', ['signalium/react']],
        ['useReactiveDeep', ['signalium/react']],
    ], opts?.transformedImports, opts?.importPaths);
    const t = api.types;
    const callbackImportPath = opts?.callbackImportPath ?? 'signalium';
    const isTrackedImport = (localName, path) => {
        const binding = path.scope.getBinding(localName);
        if (!binding || !t.isImportSpecifier(binding.path.node))
            return false;
        const importSpec = binding.path.node;
        const importedName = importSpec.imported.name;
        const importDecl = binding.path.parent;
        if (!t.isImportDeclaration(importDecl))
            return false;
        const importPaths = transformedImports.get(importedName);
        if (!importPaths)
            return false;
        const matches = importPaths.find(p => typeof p === 'string' ? importDecl.source.value === p : p.test(importDecl.source.value));
        return matches ? (typeof matches === 'string' ? matches : importDecl.source.value) : false;
    };
    const isTargetWrapperCall = (path) => {
        if (!t.isCallExpression(path.node))
            return false;
        const callee = path.node.callee;
        if (!t.isIdentifier(callee))
            return false;
        return !!isTrackedImport(callee.name, path);
    };
    function isIdentifierInTypePosition(refPath) {
        // Walk up ancestors and detect if the identifier is within TS type-only constructs
        // Allow identifiers inside the expression arm of TS* expression wrappers
        let current = refPath.parentPath;
        let child = refPath;
        while (current) {
            const nodeType = current.node.type;
            if (nodeType && nodeType.startsWith('TS')) {
                if (current.isTSAsExpression() ||
                    current.isTSSatisfiesExpression?.() ||
                    current.isTSNonNullExpression() ||
                    current.isTSInstantiationExpression?.()) {
                    // If we reached this TS* expression via its 'expression' arm, it's runtime, not type-only
                    if (child.key === 'expression')
                        return false;
                    return true;
                }
                return true;
            }
            child = current;
            current = current.parentPath;
        }
        return false;
    }
    function lookupCallbackIdentifier(programPath) {
        // Find an existing direct import: import { callback as X } from 'signalium'
        for (const bodyPath of programPath.get('body')) {
            if (!bodyPath.isImportDeclaration())
                continue;
            const importDecl = bodyPath.node;
            if (importDecl.source.value !== callbackImportPath)
                continue;
            for (const spec of importDecl.specifiers) {
                if (spec.type === 'ImportSpecifier') {
                    const ispec = spec;
                    const imported = ispec.imported;
                    if (imported && imported.name === 'callback') {
                        return ispec.local.name;
                    }
                }
            }
        }
        return undefined;
    }
    function ensureCallbackIdentifier(programPath) {
        const existing = lookupCallbackIdentifier(programPath);
        if (existing !== undefined)
            return existing;
        // Try to augment an existing import from 'signalium'
        for (const bodyPath of programPath.get('body')) {
            if (!bodyPath.isImportDeclaration())
                continue;
            const node = bodyPath.node;
            if (node.source.value !== callbackImportPath)
                continue;
            // Skip `import type` declarations — adding a value specifier there would
            // make it type-only at runtime.
            if (node.importKind === 'type')
                continue;
            const localName = programPath.scope.generateUidIdentifier('callback').name;
            node.specifiers.push(t.importSpecifier(t.identifier(localName), t.identifier('callback')));
            return localName;
        }
        // Otherwise, insert a new import from 'signalium'
        const localName = 'callback';
        const importDecl = t.importDeclaration([t.importSpecifier(t.identifier(localName), t.identifier('callback'))], t.stringLiteral(callbackImportPath));
        const [first] = programPath.get('body');
        if (first) {
            first.insertBefore(importDecl);
        }
        else {
            programPath.pushContainer('body', importDecl);
        }
        return localName;
    }
    function collectDeps(innerFn) {
        const depNames = new Set();
        const innerScope = innerFn.scope;
        const innerNode = innerFn.node;
        innerFn.traverse({
            ReferencedIdentifier(refPath) {
                // Only consider refs whose nearest function parent is the inner function
                const nearestFn = refPath.getFunctionParent();
                if (!nearestFn || nearestFn.node !== innerNode)
                    return;
                const name = refPath.node.name;
                const binding = refPath.scope.getBinding(name);
                if (!binding)
                    return;
                // Ignore identifiers that appear only in type positions
                if (isIdentifierInTypePosition(refPath))
                    return;
                // Exclude module scope
                if (binding.scope.path.isProgram())
                    return;
                // Exclude identifiers declared within the inner function itself or any nested scope inside it
                let declScope = binding.scope;
                while (declScope) {
                    if (declScope === innerScope)
                        return;
                    declScope = declScope.parent;
                }
                // Exclude only the inner function's own parameters
                if (binding.kind === 'param' && binding.scope === innerScope)
                    return;
                depNames.add(name);
            },
        });
        return depNames;
    }
    return {
        name: 'signalium-transform-callback-wrapping',
        visitor: {
            CallExpression(callPath) {
                if (!isTargetWrapperCall(callPath))
                    return;
                const arg0 = callPath.get('arguments')[0];
                if (!arg0)
                    return;
                if (!(arg0.isFunctionExpression() || arg0.isArrowFunctionExpression()))
                    return;
                const outerFn = arg0;
                const programPath = callPath.findParent((p) => p.isProgram());
                // Lazily resolve the `callback` import local name so we don't add a
                // dead `import { callback } from 'signalium'` when the outer fn has no
                // inner callbacks to wrap.
                const getCallbackName = () => ensureCallbackIdentifier(programPath);
                // For the "skip if already wrapped in callback()" check: consult the
                // existing imports without forcing one to be added.
                const peekCallbackName = () => lookupCallbackIdentifier(programPath);
                // Maintain per-function counters
                const counters = new WeakMap();
                counters.set(outerFn.node, 0);
                const getNextIndexFor = (fnNode) => {
                    const current = counters.get(fnNode) ?? 0;
                    counters.set(fnNode, current + 1);
                    return current;
                };
                outerFn.traverse({
                    // Initialize counters for any function-like node when first seen
                    FunctionExpression: {
                        enter(fnPath) {
                            if (!counters.has(fnPath.node))
                                counters.set(fnPath.node, 0);
                        },
                        exit(innerFnPath) {
                            if (innerFnPath.node === outerFn.node)
                                return;
                            // Skip converting when the function is passed directly as a callback
                            // to a tracked call AND that call is nested within another tracked call.
                            const immediateParent = innerFnPath.parentPath;
                            if (immediateParent && immediateParent.isCallExpression()) {
                                const callee = immediateParent.node.callee;
                                if (t.isIdentifier(callee)) {
                                    const calleeId = callee;
                                    if (isTrackedImport(calleeId.name, immediateParent)) {
                                        let current = immediateParent.parentPath;
                                        while (current) {
                                            if (current.isCallExpression()) {
                                                const parentCallee = current.node.callee;
                                                if (t.isIdentifier(parentCallee)) {
                                                    const parentCalleeId = parentCallee;
                                                    if (isTrackedImport(parentCalleeId.name, current)) {
                                                        return; // nested direct callback – skip
                                                    }
                                                }
                                            }
                                            current = current.parentPath;
                                        }
                                    }
                                }
                            }
                            // Skip if already wrapped in callback()
                            const parent = innerFnPath.parentPath;
                            if (parent && parent.isCallExpression()) {
                                const callee = parent.node.callee;
                                if (t.isIdentifier(callee)) {
                                    const calleeId = callee;
                                    if (calleeId.name === peekCallbackName()) {
                                        return;
                                    }
                                }
                            }
                            const deps = Array.from(collectDeps(innerFnPath));
                            // Determine parent function to index against
                            const parentFn = (innerFnPath.parentPath?.getFunctionParent() || outerFn);
                            const argIndex = getNextIndexFor(parentFn.node);
                            const args = [innerFnPath.node, t.numericLiteral(argIndex)];
                            if (deps.length > 0) {
                                args.push(t.arrayExpression(deps.map(n => t.identifier(n))));
                            }
                            const wrapped = t.callExpression(t.identifier(getCallbackName()), args);
                            innerFnPath.replaceWith(wrapped);
                            innerFnPath.skip();
                        },
                    },
                    ArrowFunctionExpression: {
                        enter(fnPath) {
                            if (!counters.has(fnPath.node))
                                counters.set(fnPath.node, 0);
                        },
                        exit(innerFnPath) {
                            if (innerFnPath.node === outerFn.node)
                                return;
                            // Skip converting when the function is passed directly as a callback
                            // to a tracked call AND that call is nested within another tracked call.
                            const immediateParent = innerFnPath.parentPath;
                            if (immediateParent && immediateParent.isCallExpression()) {
                                const callee = immediateParent.node.callee;
                                if (t.isIdentifier(callee)) {
                                    const calleeId = callee;
                                    if (isTrackedImport(calleeId.name, immediateParent)) {
                                        let current = immediateParent.parentPath;
                                        while (current) {
                                            if (current.isCallExpression()) {
                                                const parentCallee = current.node.callee;
                                                if (t.isIdentifier(parentCallee)) {
                                                    const parentCalleeId = parentCallee;
                                                    if (isTrackedImport(parentCalleeId.name, current)) {
                                                        return; // nested direct callback – skip
                                                    }
                                                }
                                            }
                                            current = current.parentPath;
                                        }
                                    }
                                }
                            }
                            // Skip if already wrapped in callback()
                            const parent = innerFnPath.parentPath;
                            if (parent && parent.isCallExpression()) {
                                const callee = parent.node.callee;
                                if (t.isIdentifier(callee)) {
                                    const calleeId = callee;
                                    if (calleeId.name === peekCallbackName()) {
                                        return;
                                    }
                                }
                            }
                            const deps = Array.from(collectDeps(innerFnPath));
                            const parentFn = (innerFnPath.parentPath?.getFunctionParent() || outerFn);
                            const argIndex = getNextIndexFor(parentFn.node);
                            const args = [innerFnPath.node, t.numericLiteral(argIndex)];
                            if (deps.length > 0) {
                                args.push(t.arrayExpression(deps.map(n => t.identifier(n))));
                            }
                            const wrapped = t.callExpression(t.identifier(getCallbackName()), args);
                            innerFnPath.replaceWith(wrapped);
                            innerFnPath.skip();
                        },
                    },
                    FunctionDeclaration: {
                        enter(fnPath) {
                            if (!counters.has(fnPath.node))
                                counters.set(fnPath.node, 0);
                        },
                        exit(innerDeclPath) {
                            const id = innerDeclPath.node.id;
                            if (!id)
                                return;
                            const fnExpr = t.functionExpression(id, innerDeclPath.node.params, innerDeclPath.node.body, innerDeclPath.node.generator, innerDeclPath.node.async);
                            const deps = Array.from(collectDeps(innerDeclPath));
                            const parentFn = (innerDeclPath.parentPath?.getFunctionParent() || outerFn);
                            const argIndex = getNextIndexFor(parentFn.node);
                            const args = [fnExpr, t.numericLiteral(argIndex)];
                            if (deps.length > 0) {
                                args.push(t.arrayExpression(deps.map(n => t.identifier(n))));
                            }
                            const wrapped = t.callExpression(t.identifier(getCallbackName()), args);
                            const constDecl = t.variableDeclaration('const', [t.variableDeclarator(id, wrapped)]);
                            innerDeclPath.replaceWith(constDecl);
                            innerDeclPath.skip();
                        },
                    },
                    ObjectMethod: {
                        enter(fnPath) {
                            if (!counters.has(fnPath.node))
                                counters.set(fnPath.node, 0);
                        },
                        exit(innerMethodPath) {
                            if (innerMethodPath.node.kind !== 'method')
                                return;
                            const fnExpr = t.functionExpression(null, innerMethodPath.node.params, innerMethodPath.node.body, innerMethodPath.node.generator, innerMethodPath.node.async);
                            const deps = Array.from(collectDeps(innerMethodPath));
                            const parentFn = (innerMethodPath.parentPath?.getFunctionParent() || outerFn);
                            const argIndex = getNextIndexFor(parentFn.node);
                            const args = [fnExpr, t.numericLiteral(argIndex)];
                            if (deps.length > 0) {
                                args.push(t.arrayExpression(deps.map(n => t.identifier(n))));
                            }
                            const wrapped = t.callExpression(t.identifier(getCallbackName()), args);
                            const key = innerMethodPath.node.key;
                            const computed = innerMethodPath.node.computed || false;
                            const prop = t.objectProperty(key, wrapped, computed);
                            innerMethodPath.replaceWith(prop);
                            innerMethodPath.skip();
                        },
                    },
                });
            },
        },
    };
}
export function signaliumCallbackTransform(apiOrOpts, opts) {
    if (isBabelApi(apiOrOpts)) {
        return createSignaliumCallbackTransform(apiOrOpts, opts);
    }
    else {
        return (api) => createSignaliumCallbackTransform(api, apiOrOpts);
    }
}
